import { Badge, Button, HStack, Text } from '@chakra-ui/react'
import { Plus } from 'lucide-react'
import type { ChildTicketTask } from '@/lib/services/childTicketService'
import { useThemeColors } from '@/hooks/useThemeColors'
import {
  findPrimaryTaskForSourceField,
  filterTasksBySourceField,
  type TaskSourceField,
} from './taskSourceFieldUtils'
import { resolveTaskStatusLabel } from './taskUtils'

type Props = {
  sourceField: TaskSourceField
  tasks: ChildTicketTask[]
  onCreate?: (sourceField: TaskSourceField) => void
  onOpenTask: (task: ChildTicketTask) => void
  disabled?: boolean
  /** When false, hide Create task and only show a linked-task status badge. Default true. */
  allowCreate?: boolean
}

function resolveStatusLabel(task: ChildTicketTask): string {
  return resolveTaskStatusLabel(task)
}

export default function TaskBadge({
  sourceField,
  tasks,
  onCreate,
  onOpenTask,
  disabled,
  allowCreate = true,
}: Props) {
  const colors = useThemeColors()
  const linkedTask = findPrimaryTaskForSourceField(tasks, sourceField)

  if (!linkedTask) {
    if (!allowCreate || !onCreate) return null
    return (
      <Button
        size="xs"
        variant="outline"
        colorScheme="blue"
        leftIcon={<Plus size={12} />}
        onClick={() => onCreate(sourceField)}
        isDisabled={disabled}
      >
        Create task
      </Button>
    )
  }

  const taskLabel = linkedTask.task_number?.trim() || `Task #${linkedTask.id}`
  const statusLabel = resolveStatusLabel(linkedTask)

  return (
    <HStack spacing={1}>
      <Badge
        as="button"
        type="button"
        colorScheme="blue"
        variant="subtle"
        textTransform="none"
        fontSize="xs"
        px={2}
        py={1}
        borderRadius="md"
        cursor={disabled ? 'not-allowed' : 'pointer'}
        opacity={disabled ? 0.6 : 1}
        onClick={() => {
          if (!disabled) onOpenTask(linkedTask)
        }}
        _hover={disabled ? undefined : { bg: 'blue.100' }}
      >
        {taskLabel} · {statusLabel}
      </Badge>
      {filterTasksBySourceField(tasks, sourceField).length > 1 ? (
        <Text fontSize="xs" color={colors.textMuted}>
          +{filterTasksBySourceField(tasks, sourceField).length - 1} more
        </Text>
      ) : null}
    </HStack>
  )
}
